import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { affiliationAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { FileClock, AlertTriangle, RefreshCw, Ban, Upload } from 'lucide-react';
import ProtectedResumeLink from '../components/afiliacion/ProtectedResumeLink';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AfiliacionRetomarPage = () => {
  const { token: initialToken } = useParams();
  const [token, setToken] = useState(initialToken);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ full_name: '', phone: '', state: '', company_name: '' });
  const [saved, setSaved] = useState(false);
  const [docs, setDocs] = useState([]);
  const [notice, setNotice] = useState('');
  const [newLink, setNewLink] = useState('');
  const [revoked, setRevoked] = useState(false);
  const [isProduction, setIsProduction] = useState(false);

  const load = (t) => {
    affiliationAPI.resumeGet(t)
      .then((d) => {
        setData(d);
        setForm({ full_name: d.full_name || '', phone: d.phone || '', state: d.state || '', company_name: d.company_name || '' });
        affiliationAPI.resumeDocs(t).then(setDocs).catch(() => setDocs([]));
      })
      .catch((e) => setError(e.message));
  };

  useEffect(() => { load(token); }, [token]);

  const save = async () => {
    setError('');
    try { await affiliationAPI.resumePut(token, form); setSaved(true); load(token); }
    catch (e) { setError(e.message); }
  };

  const renew = async () => {
    setError('');
    try {
      const r = await affiliationAPI.resumeRenew(token);
      setNewLink(r.resume_url);
      setIsProduction((r.env || 'development') === 'production');
      setToken(r.resume_token);
      setNotice('Enlace renovado. Guarda el nuevo enlace de reanudación.');
    } catch (e) { setError(e.message); }
  };

  const revoke = async () => {
    setError('');
    try { await affiliationAPI.resumeRevoke(token); setRevoked(true); }
    catch (e) { setError(e.message); }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const resp = await fetch(`${API_URL}/api/affiliation/requests/resume/documents?token=${encodeURIComponent(token)}&doc_type=identificacion_oficial`, {
        method: 'POST', body: fd,
      });
      if (!resp.ok) { const j = await resp.json().catch(() => ({})); throw new Error(j.detail || 'Error al subir'); }
      affiliationAPI.resumeDocs(token).then(setDocs).catch(() => {});
      setNotice('Documento cargado correctamente.');
    } catch (err) { setError(err.message); }
  };

  if (revoked) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div data-testid="resume-revoked" className="max-w-md bg-white rounded-2xl border p-8 text-center">
          <Ban className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <p className="text-gray-700">Este enlace fue revocado y ya no puede usarse.</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div data-testid="resume-error" className="max-w-md bg-white rounded-2xl border p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <p className="text-gray-700">Este enlace no es válido o expiró.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-2xl border shadow-sm p-8">
        <FileClock className="h-10 w-10 text-gray-900 mb-3" />
        <h1 className="text-2xl font-bold text-gray-900">Retomar tu solicitud</h1>
        {error && <div data-testid="resume-inline-error" className="mt-3 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
        {notice && <div data-testid="resume-notice" className="mt-3 bg-blue-50 text-blue-700 rounded-lg p-3 text-sm">{notice}</div>}
        {data && (
          <div data-testid="resume-data" className="mt-4 space-y-4">
            <p className="text-sm text-gray-500">Folio</p>
            <p className="text-xl font-mono font-bold">{data.folio}</p>
            <p className="text-sm text-gray-500">Estado actual: <span className="font-medium text-gray-800">{data.status}</span></p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <input data-testid="resume-name" placeholder="Nombre completo" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="border rounded-lg px-4 py-2.5" />
              <input data-testid="resume-phone" placeholder="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border rounded-lg px-4 py-2.5" />
              <input data-testid="resume-state" placeholder="Estado" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="border rounded-lg px-4 py-2.5" />
              <input data-testid="resume-company" placeholder="Razón social (opcional)" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="border rounded-lg px-4 py-2.5" />
            </div>
            <Button data-testid="resume-save" onClick={save} className="w-full">Guardar y continuar</Button>
            {saved && <p data-testid="resume-saved" className="text-green-600 text-sm">Cambios guardados.</p>}

            {/* Documentos */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Documentos ({docs.length})</p>
              <ul className="text-sm text-gray-600 space-y-1 mb-3">
                {docs.map((d) => <li key={d.id} data-testid="resume-doc">{d.filename} — {d.review_status}</li>)}
              </ul>
              <label className="inline-flex items-center gap-2 border rounded-lg px-4 py-2.5 text-sm cursor-pointer hover:bg-gray-50" data-testid="resume-upload-label">
                <Upload className="h-4 w-4" /> Subir documento (PDF/JPG/PNG)
                <input data-testid="resume-upload" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadDoc} className="hidden" />
              </label>
            </div>

            {/* Renovar / Revocar enlace */}
            <div className="border-t pt-4 flex flex-wrap gap-2">
              <Button variant="outline" data-testid="resume-renew" onClick={renew}><RefreshCw className="h-4 w-4 mr-2" /> Renovar enlace</Button>
              <Button variant="outline" className="text-red-600" data-testid="resume-revoke" onClick={revoke}><Ban className="h-4 w-4 mr-2" /> Revocar enlace</Button>
            </div>
            {newLink && (
              <div data-testid="resume-new-link" className="space-y-2">
                <ProtectedResumeLink url={newLink} isProduction={isProduction} testid="resume-link" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AfiliacionRetomarPage;
